import { useState } from 'react';
import { format, addDays } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { cn } from '@/core/lib/utils';
import { Calendar } from '@/shared/components/ui/Calendar';
import { Switch } from '@/shared/components/ui/switch';
import { TimeSelect } from '@/shared/components/ui/TimeSelect';
import { Input } from '@/shared/components/ui/input';
import type { DateFormat, TimeFormat } from '@/core/stores/ui.store';

const PRESETS = [
  { labelKey: 'eventFicha.datePicker.presetToday', days: 0 },
  { labelKey: 'eventFicha.datePicker.presetTomorrow', days: 1 },
  { labelKey: 'eventFicha.datePicker.presetNextWeek', days: 7 },
  { labelKey: 'eventFicha.datePicker.presetTwoWeeks', days: 14 },
] as const;

function buildDate(dateStr: string, timeStr: string): Date | null {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = (timeStr || '00:00').split(':').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0);
}

interface EventDatePickerProps {
  start: Date;
  end: Date;
  allDay: boolean;
  onSave: (start: Date, end: Date, allDay: boolean) => void;
  disabled?: boolean;
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
}

/** Mirrors `TaskDatePicker` (raw Dialog popup: presets · manual inputs · calendar · all-day toggle). */
export function EventDatePicker({
  start,
  end,
  allDay,
  onSave,
  disabled,
  dateFormat,
  timeFormat,
}: EventDatePickerProps) {
  const { t, i18n } = useTranslation('calendar');
  const locale = i18n.language.startsWith('es') ? es : enUS;
  const [open, setOpen] = useState(false);

  const [localStartDate, setLocalStartDate] = useState('');
  const [localEndDate, setLocalEndDate] = useState('');
  const [localStartTime, setLocalStartTime] = useState('');
  const [localEndTime, setLocalEndTime] = useState('');
  const [localAllDay, setLocalAllDay] = useState(allDay);
  const [activeField, setActiveField] = useState<'start' | 'end'>('start');

  function handleOpenChange(isOpen: boolean) {
    if (isOpen) {
      setLocalStartDate(format(start, 'yyyy-MM-dd'));
      setLocalEndDate(format(end, 'yyyy-MM-dd'));
      setLocalStartTime(format(start, 'HH:mm'));
      setLocalEndTime(format(end, 'HH:mm'));
      setLocalAllDay(allDay);
      setActiveField('start');
    } else {
      const nextStart = buildDate(localStartDate, localAllDay ? '00:00' : localStartTime);
      const nextEnd = buildDate(localEndDate, localAllDay ? '00:00' : localEndTime);
      if (nextStart && nextEnd) {
        const changed =
          nextStart.getTime() !== start.getTime() ||
          nextEnd.getTime() !== end.getTime() ||
          localAllDay !== allDay;
        if (changed) onSave(nextStart, nextEnd, localAllDay);
      }
    }
    setOpen(isOpen);
  }

  function handleCalendarSelect(date: Date | undefined) {
    if (!date) return;
    const iso = format(date, 'yyyy-MM-dd');
    if (activeField === 'start') {
      setLocalStartDate(iso);
      if (localEndDate && iso > localEndDate) setLocalEndDate(iso);
    } else {
      setLocalEndDate(iso);
      if (localStartDate && iso < localStartDate) setLocalStartDate(iso);
    }
  }

  function applyPreset(days: number) {
    const iso = format(addDays(new Date(), days), 'yyyy-MM-dd');
    setLocalStartDate(iso);
    setLocalEndDate(iso);
    setActiveField('start');
  }

  const rangeLabel = allDay
    ? format(start, dateFormat, { locale })
    : `${format(start, dateFormat, { locale })} ${format(start, timeFormat, { locale })} – ${format(end, timeFormat, { locale })}`;

  const parsedStart = localStartDate ? new Date(`${localStartDate}T12:00:00`) : undefined;
  const parsedEnd = localEndDate ? new Date(`${localEndDate}T12:00:00`) : undefined;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Trigger asChild disabled={disabled}>
        <button
          type="button"
          data-testid="calendar-event-date-picker-trigger"
          className="flex items-center rounded-md border border-input bg-background px-2 py-1 text-xs transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
          onClick={(e) => e.stopPropagation()}
        >
          {rangeLabel}
        </button>
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/20" />
        <DialogPrimitive.Content
          data-testid="calendar-event-date-picker-popover"
          className={cn(
            'fixed top-1/2 left-1/2 z-50 w-80 -translate-x-1/2 -translate-y-1/2 sm:w-110',
            'rounded-lg border bg-background p-0 shadow-lg outline-none',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          )}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <DialogPrimitive.Title className="sr-only">
            {t('eventFicha.dateTimeLabel')}
          </DialogPrimitive.Title>

          <div className="flex items-center justify-between border-b px-3 py-2.5">
            <span className="text-sm font-semibold">{t('eventFicha.dateTimeLabel')}</span>
          </div>

          <div className="flex flex-col sm:grid sm:grid-cols-[140px_1fr] sm:divide-x">
            <div className="border-b p-3 sm:border-b-0">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('eventFicha.datePicker.presets')}
              </p>
              <div className="flex flex-col gap-0.5">
                {PRESETS.map((preset) => (
                  <button
                    type="button"
                    key={preset.labelKey}
                    onClick={() => applyPreset(preset.days)}
                    className="flex flex-col rounded px-2 py-1.5 text-left transition-colors hover:bg-accent"
                  >
                    <span className="text-xs">{t(preset.labelKey)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2.5 p-3">
              <div>
                <p className="mb-1 text-[10px] text-muted-foreground">
                  {t('eventFicha.datePicker.startDate')}
                </p>
                <div className="flex items-center gap-1.5">
                  <Input
                    value={localStartDate}
                    onChange={(e) => setLocalStartDate(e.target.value)}
                    onFocus={() => setActiveField('start')}
                    className="h-7 text-xs"
                    data-testid="calendar-event-date-picker-start-input"
                  />
                  {!localAllDay && (
                    <TimeSelect
                      value={localStartTime}
                      timeFormat={timeFormat}
                      onChange={setLocalStartTime}
                      testId="calendar-event-date-picker-start-time"
                    />
                  )}
                </div>
              </div>

              <div>
                <p className="mb-1 text-[10px] text-muted-foreground">
                  {t('eventFicha.datePicker.endDate')}
                </p>
                <div className="flex items-center gap-1.5">
                  <Input
                    value={localEndDate}
                    onChange={(e) => setLocalEndDate(e.target.value)}
                    onFocus={() => setActiveField('end')}
                    className="h-7 text-xs"
                    data-testid="calendar-event-date-picker-end-input"
                  />
                  {!localAllDay && (
                    <TimeSelect
                      value={localEndTime}
                      timeFormat={timeFormat}
                      onChange={setLocalEndTime}
                      testId="calendar-event-date-picker-end-time"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t">
            <Calendar
              key={activeField}
              rangeStart={parsedStart}
              rangeEnd={parsedEnd}
              onSelect={handleCalendarSelect}
              data-testid="calendar-event-date-picker-calendar"
            />
          </div>

          <div className="flex items-center gap-1.5 border-t px-3 py-2">
            <span className="text-xs">{t('eventFicha.datePicker.allDayToggle')}</span>
            <Switch
              size="sm"
              checked={localAllDay}
              onCheckedChange={setLocalAllDay}
              data-testid="calendar-event-date-picker-allday-switch"
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
